import clsx from 'clsx';
import React, { useCallback, useContext, useMemo } from 'react';
import { TypographyStyles } from 'src/styles';
import Style from './surveyTableRow.module.css';
import { HeaderTableRowProps } from './surveyAnswers';
import { DateFormats, localFormat } from 'src/helpers/date-helpers';
import { ReactComponent as RightArrowSmall } from '../../assets/img/survey/rightArrowSmall.svg';
import { ReactComponent as LeftArrowSmall } from '../../assets/img/survey/leftArrowSmall.svg';
import { ReactComponent as Doctor } from '../../assets/img/survey/doctor_16.svg';
import { AppButton } from 'src/components/uikit/buttons/button/button.component';
import { AppSwiper } from 'src/components/swiper/appSwiper.component';
import { slideWidthPx } from './constants';
import { SurveyAnswersColumnsContext } from './surveyAnswersLayout';
import { Tooltip } from '../../components/uikit/tooltip/tooltip.component';
import { SwiperSlide } from 'swiper/react';
import { useScopedTranslation } from '../../application/localisation/useScopedTranslation';

export const HeaderTableRow = (props: HeaderTableRowProps) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.SurveySection');
  const visibleColumns = useContext(SurveyAnswersColumnsContext);
  const scoreComponentList = props.data.map((data, i) => {
    if (!data.date) {
      return <SwiperSlide className={clsx(Style.answer, { [Style.singleAnswer]: props.isSingleAnswer })} key={i} />;
    }

    return (
      <SwiperSlide className={clsx(Style.answer, Style.header, { [Style.singleAnswer]: props.isSingleAnswer })} key={i}>
        {data.answeredBy && (
          <Tooltip text={t('LastAvailableStats.FilledBy', { doctor: data.answeredBy.fullName })}>
            <Doctor />
          </Tooltip>
        )}
        <div data-test-id={'survey-answer-date'}>{localFormat(data.date, DateFormats.longDate)}</div>
      </SwiperSlide>
    );
  });

  const isNextDisabled = useMemo(() => {
    return (props.swipeIndex ?? 0) + visibleColumns >= props.data.length;
  }, [props.data.length, props.swipeIndex, visibleColumns]);

  const isPrevDisabled = useMemo(() => {
    return (props.swipeIndex ?? 0) <= 0;
  }, [props.swipeIndex]);

  const goNext = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    props.onSwipe(Math.min((props.swipeIndex ?? 0) + 1, Math.max(0, props.data.length - visibleColumns)));
  }, [props, visibleColumns]);

  const goPrev = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    props.onSwipe(Math.max(0, (props.swipeIndex ?? 0) - 1));
  }, [props]);

  return (
    <div className={clsx(TypographyStyles.plainText14, Style.container)}>
      <div className={Style.row}>
        <div className={Style.index} />
        <div className={Style.questionTextContainer}>
          <div className={Style.questionText} />
          {scoreComponentList.length > visibleColumns && (
            <AppButton
              disabled={isPrevDisabled}
              variant={'icon-link'}
              colorSchema={'primary'}
              Icon={LeftArrowSmall}
              onClick={goPrev}
            />
          )}
        </div>
        <AppSwiper
          slideWidth={props.isSingleAnswer ? 200 : slideWidthPx}
          slidesPerView={props.isSingleAnswer ? 1 : visibleColumns}
          containerStyles={Style.answersContainer}
          swipeIndex={props.swipeIndex}
        >
          {scoreComponentList}
        </AppSwiper>
        <div className={Style.lastColumn}>
          {scoreComponentList.length > visibleColumns && (
            <AppButton
              disabled={isNextDisabled}
              variant={'icon-link'}
              colorSchema={'primary'}
              Icon={RightArrowSmall}
              onClick={goNext}
            />
          )}
        </div>
      </div>
    </div>
  );
};
